'use client'

import { useCallback, useEffect, useId, useRef, useState } from 'react'

import { formatSecondsToTime } from '@/lib/utils'

import { DEFAULT_AUDIO_STATUS, type AudioStatus, type RegisteredPlayer } from './audio-context'
import { useAudio } from './use-audio'

interface UseAudioPlayerProps {
  url: string
  group?: string
  crossOrigin?: '' | 'anonymous' | 'use-credentials'
  onPlayError?: (error: unknown) => void
}

export const useAudioPlayer = ({ url, group, crossOrigin, onPlayError }: UseAudioPlayerProps) => {
  const id = useId()
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const playerRef = useRef<RegisteredPlayer | null>(null)
  const { register, deregister } = useAudio()
  const [status, setStatus] = useState<AudioStatus>(DEFAULT_AUDIO_STATUS)
  const [isMounted, setIsMounted] = useState(false)
  const errorHandler = useRef(onPlayError)

  useEffect(() => {
    errorHandler.current = onPlayError
  }, [onPlayError])

  useEffect(() => {
    setIsMounted(true)
  }, [])

  useEffect(() => {
    if (!isMounted || !audioRef.current) return

    const player = register({
      id,
      audio: audioRef.current,
      group,
      onUpdate: setStatus,
      onPlayError: (error) => errorHandler.current?.(error),
    })
    playerRef.current = player

    return () => {
      deregister(id)
      playerRef.current = null
    }
  }, [id, group, register, deregister, isMounted])

  const toggle = useCallback(() => {
    playerRef.current?.toggle()
  }, [])

  return {
    audioRef,
    status,
    toggle,
    isMounted,
    formattedCurrentTime: formatSecondsToTime(status.currentTime),
    formattedDuration: formatSecondsToTime(status.duration),
    audioProps: {
      src: url,
      preload: 'none' as const,
      ...(crossOrigin !== undefined ? { crossOrigin } : {}),
    },
  }
}
